import {Authorize, useUserContext} from '@goschool/auth'
import {useCourseContext} from '@goschool/dao'
import {GoSchool} from '@goschool/routing'
import ChatIcon from '@mui/icons-material/Chat'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import GroupsIcon from '@mui/icons-material/Groups'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import type {IconButtonProps} from '@mui/material'
import {Card, CardContent, CardHeader, Grid2 as Grid, IconButton, Stack, Tooltip} from '@mui/material'
import type {DocumentReference, QueryDocumentSnapshot} from 'firebase/firestore'
import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {CourseDescription, InstructorActionLink} from './components'
import type {Course} from '@goschool/model'
import {useCourseChats, useUserCourseChats} from '@goschool/model'
import {InstructorInvitationDialog, StudentInvitation} from '../components/CourseInvitation'
import type {SvgIconComponent} from '@mui/icons-material'
import {CourseChatsCard} from './components/CourseChatsCard'
import EditIcon from '@mui/icons-material/Edit'
import AssistantIcon from '@mui/icons-material/Assistant'
import {useNavigate} from 'react-router-dom'
import PersonAddIcon from '@mui/icons-material/PersonAdd'

export function InstructorCourse({snapshot}: { snapshot: QueryDocumentSnapshot<Course> }) {
  const [course, ref] = useMemo(
    () => [snapshot === null ? null : snapshot?.data(), snapshot?.ref],
    [snapshot]
  )
  const {user} = useUserContext()

  const {t} = useTranslation()
  const studentChats = useCourseChats(snapshot, 3)
  const chats = useUserCourseChats(snapshot, user, 3)

  return <Stack direction="column" gap={4}>
    <InstructorToolbar courseRef={ref}/>
    <Grid container spacing={2} rowSpacing={2}>
      <Grid size={{xs: 12, md: 6, lg: 8}}>
        <Stack direction="column" gap={2}>
          <Card>
            <CardHeader title={t('course:details.description')}/>
            <CardContent>
              <CourseDescription>{course?.description}</CourseDescription>
            </CardContent>
          </Card>
        </Stack>
      </Grid>
      <Grid size={{xs: 12, md: 6, lg: 4}}>
        <Stack direction="column" gap={2}>
          <Card>
            <CardHeader title={t('auth:invitations.studentInvitation')}/>
            <CardContent><StudentInvitation/></CardContent>
          </Card>
          <CourseChatsCard
            title={t('course:details.recentStudentChats')}
            chats={studentChats}/>
          <CourseChatsCard
            chats={chats} title={t('course:details.recentChats')}/>
        </Stack>
      </Grid>
    </Grid>
  </Stack>
}


interface InstructorActionButtonProps extends Omit<IconButtonProps, 'size' | 'color'> {
  Icon: SvgIconComponent;
  label: string
}

function InstructorActionIconButton({Icon, label, children, ...buttonProps}: InstructorActionButtonProps) {
  const {courseRoles} = useCourseContext()

  return <Authorize roles={['instructor', 'admin']} contextRoles={courseRoles}>
    <Tooltip title={label} arrow><IconButton color="primary"  {...buttonProps}>
      <Icon sx={{fontSize: '16px'}}/>
    </IconButton></Tooltip>
  </Authorize>
}


interface InstructorActionLinkProps extends InstructorActionButtonProps {
  to: string
}


function InstructorActioIconLink({Icon, label, children, to, ...buttonProps}: InstructorActionLinkProps) {
  const navigate = useNavigate()
  const handleClick = useCallback(
    () => navigate(to),
    [navigate, to]
  )
  return <InstructorActionIconButton Icon={Icon} onClick={handleClick} label={label} {...buttonProps}/>
}

function InstructorToolbar({courseRef}: { courseRef: DocumentReference<Course> }) {
  const {t} = useTranslation()

  const [isInviting, setIsInviting] = useState(false)

  return <Stack direction="row" gap={2} justifyContent="stretch">
    <Stack direction="row" gap={1} justifyContent="flex-start" flexGrow={1}>
      <InstructorActionLink
        variant="contained" label={t('course:actions.chat')}
        to={GoSchool.courseChat(courseRef)}
        Icon={ChatIcon}></InstructorActionLink>
      <InstructorActionLink
        variant="outlined" label={t('course:actions.studentChats')}
        to={GoSchool.courseChats(courseRef)}
        Icon={ForumOutlinedIcon}/>
    </Stack>

    <Stack direction="row" gap={1} justifyContent="flex-end" flexGrow={1}>
      <InstructorActioIconLink
        label={t('course:actions.edit')}
        to={GoSchool.courseEdit(courseRef)}
        Icon={EditIcon}/>
      <InstructorActioIconLink
        label={t('course:actions.customizeAssistant')}
        to={GoSchool.courseCustomizeAssistant(courseRef)}
        Icon={AssistantIcon}/>

      <InstructorActioIconLink
        label={t('course:actions.documents')}
        to={GoSchool.courseDocuments(courseRef)}
        Icon={MenuBookIcon}/>
      <InstructorActioIconLink
        label={t('course:actions.students')}
        to={GoSchool.courseStudents(courseRef)}
        Icon={GroupsIcon}/>
      <InstructorActionIconButton
        label={t('course:actions.inviteInstructor')}
        onClick={() => setIsInviting(true)}
        Icon={PersonAddIcon}/>
    </Stack>
    <InstructorInvitationDialog isShown={isInviting} hide={() => setIsInviting(false)}/>
  </Stack>
}
